"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AltiumLine = exports.AltiumRecordDataHelpers = void 0;
const tslib_1 = require("tslib");
const zod_1 = tslib_1.__importDefault(require("zod"));
const direction_1 = require("../../direction");
const recordSchemas_1 = require("./recordSchemas");
const zeroVector = { x: 0, y: 0 };
/**
 * Helper methods for working with Altium record data
 */
class AltiumRecordDataHelpers {
    /**
     * Retrieves a field value from an Altium record data object
     */
    static get(recordData, fieldName) {
        return recordData[fieldName];
    }
    /**
     * Parses raw record data into a typed Altium record data object
     */
    static parseRecordData(recordType, rawRecordData) {
        return recordSchemas_1.recordDataSchemaMap[recordType].passthrough().parse(rawRecordData);
    }
    /**
     * Retrieves coordinate point from an Altium record data object
     */
    static getSinglePoint(recordData) {
        if (this.isValidSinglePointRecordData(recordData)) {
            return {
                x: this.withFrac(recordData["LOCATION.X"], recordData["LOCATION.X_FRAC"]),
                y: this.withFrac(recordData["LOCATION.Y"], recordData["LOCATION.Y_FRAC"]),
            };
        }
        return zeroVector;
    }
    /**
     * Retrieves multiple coordinate points from an Altium record data object
     */
    static getMultiPoint(recordData) {
        if (this.isValidMultiPointRecordData(recordData)) {
            const points = [];
            const linesCount = recordData["LOCATIONCOUNT"];
            for (let i = 1; i <= linesCount; i++) {
                const xIntKey = `X${i}`;
                const yIntKey = `Y${i}`;
                const xFracKey = `X${i}_FRAC`;
                const yFracKey = `Y${i}_FRAC`;
                const { [xIntKey]: xInt, [yIntKey]: yInt, [xFracKey]: xFrac, [yFracKey]: yFrac, } = zod_1.default
                    .object({
                    [xIntKey]: zod_1.default.coerce.number(),
                    [yIntKey]: zod_1.default.coerce.number(),
                    [xFracKey]: zod_1.default.coerce.number().optional(),
                    [yFracKey]: zod_1.default.coerce.number().optional(),
                })
                    .parse(recordData);
                points.push({
                    x: this.withFrac(xInt, xFrac),
                    y: this.withFrac(yInt, yFrac),
                });
            }
            return points;
        }
        return [];
    }
    /**
     * Combines integer and fractional parts into a single floating point number
     */
    static withFrac(integer, fraction = 0) {
        return integer + fraction / 100_000;
    }
    /**
     * Converts points to line segments
     */
    static toLines(points) {
        const lines = [];
        for (let i = 0; i < points.length - 1; i++) {
            let start = points[i];
            let end = points[i + 1];
            if (start && end) {
                // Ensure start is always lower than end
                if ((start.x === end.x && start.y > end.y) || (start.y === end.y && start.x > end.x)) {
                    [start, end] = [end, start];
                }
                lines.push(new AltiumLine(start, end));
            }
        }
        return lines;
    }
    /**
     * Serializes a point to a string format for use as a key
     */
    static serializePoint(point) {
        return `${point.x},${point.y}`;
    }
    static isValidSinglePointRecordData(recordData) {
        return "LOCATION.X" in recordData && "LOCATION.Y" in recordData;
    }
    static isValidMultiPointRecordData(recordData) {
        return "LOCATIONCOUNT" in recordData;
    }
}
exports.AltiumRecordDataHelpers = AltiumRecordDataHelpers;
/**
 * Represents a line segment in Altium record data
 */
class AltiumLine {
    from;
    to;
    constructor(from, to) {
        this.from = from;
        this.to = to;
    }
    /**
     * Determines if the line is diagonal
     */
    isDiagonal() {
        return this.from.x !== this.to.x && this.from.y !== this.to.y;
    }
    /**
     * Determines the orientation of the line
     */
    direction() {
        if (this.from.x === this.to.x) {
            return direction_1.Orientation.VERTICAL;
        }
        if (this.from.y === this.to.y) {
            return direction_1.Orientation.HORIZONTAL;
        }
        throw new Error("Line is not horizontal or vertical");
    }
}
exports.AltiumLine = AltiumLine;
//# sourceMappingURL=recordData.js.map